import React, { useEffect, useState } from "react";
import api, { API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  BarChart3, Users, FileText, SlidersHorizontal, Mail, Tag, Download, X, Loader2,
  RefreshCw, Flag, FileDown, Percent,
} from "lucide-react";
import { CURRENCIES } from "../lib/currency";

const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "assessments", label: "Assessments", icon: FileText },
  { key: "users", label: "Users", icon: Users },
  { key: "config", label: "Scoring & Pricing", icon: SlidersHorizontal },
  { key: "emails", label: "Email Templates", icon: Mail },
  { key: "coupons", label: "Coupons", icon: Tag },
];

const card = "bg-white border border-line rounded-md";
const inputC = "w-full px-3 py-2 bg-white border border-line rounded-[3px] focus:ring-2 focus:ring-gold outline-none text-sm";

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center gap-2.5 mb-8">
        <span className="inline-block w-2.5 h-2.5 bg-gold rotate-45" />
        <h1 className="text-2xl font-bold text-navy font-display">Admin Console</h1>
      </div>
      <div className="grid lg:grid-cols-5 gap-8">
        <nav className="lg:col-span-1 flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-[3px] text-sm font-medium whitespace-nowrap transition ${tab === t.key ? "bg-navy text-ivory" : "text-charcoal/70 hover:bg-white"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="lg:col-span-4">
          {tab === "overview" && <Overview />}
          {tab === "assessments" && <Assessments />}
          {tab === "users" && <UsersTab />}
          {tab === "config" && <ConfigTab />}
          {tab === "emails" && <EmailsTab />}
          {tab === "coupons" && <CouponsTab />}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className={`${card} p-5`}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-3xl font-extrabold text-navy">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function Overview() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return <Loader2 className="animate-spin text-gold" />;
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Landing visits" value={a.landing_visits} />
        <Stat label="Assessment starts" value={a.assessment_starts} />
        <Stat label="Completions" value={a.assessment_completions} sub={`${a.completion_rate}% completion`} />
        <Stat label="Free results viewed" value={a.free_results_viewed} />
        <Stat label="Upgrade clicks" value={a.upgrade_clicks} />
        <Stat label="Checkout starts" value={a.checkout_starts} />
        <Stat label="Purchases" value={a.purchases} sub={`${a.conversion_rate}% conversion`} />
        <Stat label="Average score" value={a.average_score} sub={`${a.abandoned_assessments} abandoned`} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Focus purchases" value={a.focus_purchases ?? 0} />
        <Stat label="Blueprint purchases" value={a.blueprint_purchases ?? 0} />
        <Stat label="Focus → Blueprint upgrades" value={a.upgrades ?? 0} />
      </div>
      <div className={`${card} p-6`}>
        <h3 className="font-display font-semibold text-navy mb-4">Revenue by market</h3>
        {(!a.markets || a.markets.length === 0) ? <p className="text-sm text-muted-foreground">No purchases yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid="markets-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-line">
                  <th className="py-2 pr-3">Market</th><th className="py-2 px-2">Purchases</th><th className="py-2 px-2">Focus</th>
                  <th className="py-2 px-2">Blueprint</th><th className="py-2 px-2">Upgrades</th><th className="py-2 px-2">Revenue</th>
                  <th className="py-2 px-2">ARPU</th><th className="py-2 px-2">Methods</th>
                </tr>
              </thead>
              <tbody>
                {a.markets.map((m) => (
                  <tr key={m.currency} className="border-b border-line/60">
                    <td className="py-2 pr-3 font-semibold text-navy">{m.currency}</td>
                    <td className="py-2 px-2">{m.purchases}</td><td className="py-2 px-2">{m.focus}</td>
                    <td className="py-2 px-2">{m.blueprint}</td><td className="py-2 px-2">{m.upgrades}</td>
                    <td className="py-2 px-2 font-medium">{m.currency} {Number(m.revenue).toLocaleString()}</td>
                    <td className="py-2 px-2">{m.currency} {Number(m.arpu).toLocaleString()}</td>
                    <td className="py-2 px-2 text-xs text-muted-foreground">{Object.entries(m.methods || {}).map(([k, v]) => `${k}:${v}`).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className={`${card} p-6`}>
          <h3 className="font-display font-semibold text-navy mb-4">Most common gaps</h3>
          {a.common_gaps.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> :
            a.common_gaps.map(([g, n]) => (
              <div key={g} className="flex justify-between py-2 border-b border-line last:border-0 text-sm"><span className="text-charcoal/80">{g}</span><span className="font-semibold text-navy">{n}</span></div>
            ))}
        </div>
        <div className={`${card} p-6`}>
          <h3 className="font-display font-semibold text-navy mb-4">Most recommended pathways</h3>
          {a.top_pathways.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> :
            a.top_pathways.map(([p, n]) => (
              <div key={p} className="flex justify-between py-2 border-b border-line last:border-0 text-sm"><span className="text-charcoal/80">{p}</span><span className="font-semibold text-navy">{n}</span></div>
            ))}
        </div>
      </div>
    </div>
  );
}

function Assessments() {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/admin/assessments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    const res = await api.get("/admin/export/csv", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = "fundability_export.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!items) return <Loader2 className="animate-spin text-gold" />;
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display font-semibold text-navy text-lg">Assessments ({items.length})</h2>
        <button onClick={exportCsv} data-testid="export-csv" className="flex items-center gap-2 px-4 py-2 border border-line rounded-[3px] text-sm font-medium hover:border-gold transition"><Download size={15} /> Export CSV</button>
      </div>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead className="bg-ivory text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-3">Organization</th><th className="p-3">Status</th><th className="p-3">Score</th><th className="p-3">Paid</th><th className="p-3">Docs</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id} className="border-t border-line hover:bg-ivory/50" data-testid={`admin-assessment-${x.id}`}>
                <td className="p-3">
                  <div className="font-medium text-navy flex items-center gap-2">{x.org_name}{x.flagged_review && <Flag size={13} className="text-copper" />}</div>
                  <div className="text-xs text-muted-foreground">{x.email || "guest"}</div>
                </td>
                <td className="p-3"><span className="text-xs">{x.status}{x.report_status ? ` · ${x.report_status}` : ""}</span></td>
                <td className="p-3">{x.overall_score ?? "—"}</td>
                <td className="p-3">{x.paid ? <span className="text-forest font-medium">Yes</span> : "No"}</td>
                <td className="p-3">{x.documents}</td>
                <td className="p-3"><button onClick={() => setDetail(x.id)} className="text-navy font-medium hover:text-gold">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail && <AssessmentDetail id={detail} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function AssessmentDetail({ id, onClose }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/assessments/${id}`).then((r) => setA(r.data));
  useEffect(() => { load(); }, [id]);

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/assessments/${id}/regenerate`);
      toast.success("Generating report… this can take up to a minute.");
      let tries = 0;
      const poll = async () => {
        const { data } = await api.get(`/admin/assessments/${id}`);
        setA(data);
        if (data.report_status === "generating" && tries < 40) { tries += 1; setTimeout(poll, 3000); }
        else { setBusy(false); if (data.report_status === "ready") toast.success("Report ready"); }
      };
      poll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const flag = async () => { await api.post(`/admin/assessments/${id}/flag-review`); toast.success("Toggled review flag"); load(); };
  const pdf = async () => {
    try {
      const res = await api.get(`/assessments/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const el = document.createElement("a"); el.href = url; el.download = "report.pdf"; el.click();
    } catch { toast.error("PDF not available."); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ivory rounded-md max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-navy text-ivory px-6 py-4 flex justify-between items-center">
          <h3 className="font-display font-semibold">{a?.profile?.org_name || "Assessment"}</h3>
          <button onClick={onClose} data-testid="close-detail"><X size={20} /></button>
        </div>
        {!a ? <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-gold" /></div> : (
          <div className="p-6 space-y-5">
            <div className="flex flex-wrap gap-2">
              <button onClick={regenerate} disabled={busy} data-testid="regenerate-report" className="flex items-center gap-1.5 px-3 py-2 btn-gold rounded-[3px] text-sm font-semibold">{busy ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Regenerate report</button>
              <button onClick={flag} className="flex items-center gap-1.5 px-3 py-2 border border-line rounded-[3px] text-sm"><Flag size={14} /> {a.flagged_review ? "Unflag" : "Flag"} review</button>
              {a.paid && <button onClick={pdf} className="flex items-center gap-1.5 px-3 py-2 border border-line rounded-[3px] text-sm"><FileDown size={14} /> Download PDF</button>}
            </div>
            <div>
              <h4 className="font-display font-semibold text-navy mb-2">Scores</h4>
              <p className="text-sm">Overall: <b>{a.scores?.overall_score ?? "—"}</b> · {a.readiness_category || "—"}</p>
              <pre className="mt-2 text-xs bg-white border border-line p-3 rounded overflow-x-auto">{JSON.stringify(a.scores?.category_scores || {}, null, 2)}</pre>
            </div>
            <div>
              <h4 className="font-display font-semibold text-navy mb-2">Profile</h4>
              <div className="text-sm space-y-1">
                {Object.entries(a.profile || {}).map(([k, v]) => (
                  <div key={k} className="flex gap-2"><span className="text-muted-foreground min-w-[140px]">{k}:</span><span className="text-charcoal/80">{String(v)}</span></div>
                ))}
              </div>
            </div>
            {(a.documents || []).length > 0 && (
              <div>
                <h4 className="font-display font-semibold text-navy mb-2">Documents</h4>
                {a.documents.map((d) => (
                  <a key={d.id} href={`${API}/documents/${id}/${d.id}`} target="_blank" rel="noreferrer" className="block text-sm text-navy underline">{d.doc_type} — {d.filename}</a>
                ))}
              </div>
            )}
            <div>
              <h4 className="font-display font-semibold text-navy mb-2">Answers</h4>
              <pre className="text-xs bg-white border border-line p-3 rounded overflow-x-auto max-h-40">{JSON.stringify(a.answers || {}, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  if (!users) return <Loader2 className="animate-spin text-gold" />;
  return (
    <div className={`${card} overflow-x-auto`}>
      <table className="w-full text-sm">
        <thead className="bg-ivory text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Joined</th></tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-t border-line">
              <td className="p-3 text-navy font-medium">{u.name || "—"}</td>
              <td className="p-3">{u.email}</td>
              <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-navy text-ivory" : "bg-line text-charcoal"}`}>{u.role}</span></td>
              <td className="p-3 text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConfigTab() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/config").then((r) => setConfig(r.data)); }, []);

  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put("/admin/config", patch); setConfig(data); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!config) return <Loader2 className="animate-spin text-gold" />;
  const weightSum = Object.values(config.category_weights).reduce((s, v) => s + Number(v || 0), 0);

  return (
    <div className="space-y-6">
      <div className={`${card} p-6`}>
        <h3 className="font-display font-semibold text-navy mb-1">Regional pricing</h3>
        <p className="text-xs text-muted-foreground mb-4">Intentional per-market prices (not FX conversions). Edit any currency independently. Launch price shows while active; the regular price applies once launch is off or the end date passes.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-line">
                <th className="py-2 pr-3">Currency</th>
                <th className="py-2 px-2">Focus</th>
                <th className="py-2 px-2">Blueprint launch</th>
                <th className="py-2 px-2">Blueprint regular</th>
                <th className="py-2 px-2">Launch on</th>
                <th className="py-2 px-2">Launch ends</th>
              </tr>
            </thead>
            <tbody>
              {CURRENCIES.map((c) => {
                const t = config.pricing.tiers;
                const launch = (t.full.launch && t.full.launch[c]) || { active: true, ends: null };
                const upd = (mut) => { const p = JSON.parse(JSON.stringify(config.pricing)); mut(p); setConfig({ ...config, pricing: p }); };
                return (
                  <tr key={c} className="border-b border-line/60" data-testid={`pricing-row-${c}`}>
                    <td className="py-2 pr-3 font-semibold text-navy uppercase">{c}</td>
                    <td className="py-2 px-2"><input type="number" className={`${inputC} w-28`} value={t.focus.prices[c] ?? ""} data-testid={`price-focus-${c}`}
                      onChange={(e) => upd((p) => { p.tiers.focus.prices[c] = Number(e.target.value); })} /></td>
                    <td className="py-2 px-2"><input type="number" className={`${inputC} w-28`} value={t.full.launch_prices[c] ?? ""} data-testid={`price-launch-${c}`}
                      onChange={(e) => upd((p) => { p.tiers.full.launch_prices[c] = Number(e.target.value); })} /></td>
                    <td className="py-2 px-2"><input type="number" className={`${inputC} w-28`} value={t.full.regular_prices[c] ?? ""} data-testid={`price-regular-${c}`}
                      onChange={(e) => upd((p) => { p.tiers.full.regular_prices[c] = Number(e.target.value); })} /></td>
                    <td className="py-2 px-2 text-center"><input type="checkbox" className="w-4 h-4 accent-gold" checked={!!launch.active} data-testid={`launch-active-${c}`}
                      onChange={(e) => upd((p) => { p.tiers.full.launch = p.tiers.full.launch || {}; p.tiers.full.launch[c] = { ...(p.tiers.full.launch[c] || {}), active: e.target.checked }; })} /></td>
                    <td className="py-2 px-2"><input type="date" className={`${inputC} w-40`} value={launch.ends ? String(launch.ends).slice(0, 10) : ""} data-testid={`launch-ends-${c}`}
                      onChange={(e) => upd((p) => { p.tiers.full.launch = p.tiers.full.launch || {}; p.tiers.full.launch[c] = { ...(p.tiers.full.launch[c] || {}), ends: e.target.value ? new Date(e.target.value).toISOString() : null }; })} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <button onClick={() => save({ pricing: config.pricing })} className="mt-4 btn-gold px-5 py-2 rounded-[3px] text-sm font-semibold" data-testid="save-pricing">Save pricing</button>
      </div>

      <div className={`${card} p-6`}>
        <h3 className="font-display font-semibold text-navy mb-1">Category weights</h3>
        <p className={`text-xs mb-4 ${weightSum === 100 ? "text-forest" : "text-copper"}`}>Sum: {weightSum} (should equal 100)</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {config.categories.map((c) => (
            <div key={c.key} className="flex items-center justify-between gap-3">
              <span className="text-sm text-charcoal/80">{c.name}</span>
              <input type="number" className={`${inputC} w-24`} value={config.category_weights[c.key]}
                onChange={(e) => setConfig({ ...config, category_weights: { ...config.category_weights, [c.key]: Number(e.target.value) } })} data-testid={`weight-${c.key}`} />
            </div>
          ))}
        </div>
        <button onClick={() => save({ category_weights: config.category_weights })} className="mt-4 btn-gold px-5 py-2 rounded-[3px] text-sm font-semibold" data-testid="save-weights">Save weights</button>
      </div>

      <div className={`${card} p-6`}>
        <h3 className="font-display font-semibold text-navy mb-4">Score thresholds</h3>
        <div className="space-y-2">
          {config.thresholds.map((t, i) => (
            <div key={i} className="flex items-center gap-3">
              <input type="number" className={`${inputC} w-20`} value={t.min} onChange={(e) => { const th = [...config.thresholds]; th[i] = { ...t, min: Number(e.target.value) }; setConfig({ ...config, thresholds: th }); }} />
              <span className="text-muted-foreground">–</span>
              <input type="number" className={`${inputC} w-20`} value={t.max} onChange={(e) => { const th = [...config.thresholds]; th[i] = { ...t, max: Number(e.target.value) }; setConfig({ ...config, thresholds: th }); }} />
              <input className={`${inputC} flex-1`} value={t.label} onChange={(e) => { const th = [...config.thresholds]; th[i] = { ...t, label: e.target.value }; setConfig({ ...config, thresholds: th }); }} />
            </div>
          ))}
        </div>
        <button onClick={() => save({ thresholds: config.thresholds })} className="mt-4 btn-gold px-5 py-2 rounded-[3px] text-sm font-semibold" data-testid="save-thresholds">Save thresholds</button>
      </div>
    </div>
  );
}

function EmailsTab() {
  const [config, setConfig] = useState(null);
  useEffect(() => { api.get("/admin/config").then((r) => setConfig(r.data)); }, []);
  const save = async () => {
    try { await api.put("/admin/config", { email_templates: config.email_templates }); toast.success("Templates saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!config) return <Loader2 className="animate-spin text-gold" />;
  const setT = (key, field, val) => setConfig({ ...config, email_templates: { ...config.email_templates, [key]: { ...config.email_templates[key], [field]: val } } });
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">Available placeholders: {"{name} {org_name} {score} {category} {link} {pdf_link}"}</p>
      {Object.entries(config.email_templates).map(([key, tpl]) => (
        <div key={key} className={`${card} p-5`}>
          <h4 className="font-display font-semibold text-navy capitalize mb-3">{key.replace(/_/g, " ")}</h4>
          <input className={`${inputC} mb-2`} value={tpl.subject} onChange={(e) => setT(key, "subject", e.target.value)} data-testid={`email-subject-${key}`} />
          <textarea rows={4} className={inputC} value={tpl.body} onChange={(e) => setT(key, "body", e.target.value)} data-testid={`email-body-${key}`} />
        </div>
      ))}
      <button onClick={save} className="btn-gold px-6 py-2.5 rounded-[3px] text-sm font-semibold" data-testid="save-emails">Save all templates</button>
    </div>
  );
}

function CouponsTab() {
  const [items, setItems] = useState(null);
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState("");
  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!code || !percent) { toast.error("Enter a code and percentage."); return; }
    try { await api.post("/admin/coupons", { code, percent_off: Number(percent) }); toast.success("Coupon created"); setCode(""); setPercent(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (c) => { await api.delete(`/admin/coupons/${c}`); load(); };
  if (!items) return <Loader2 className="animate-spin text-gold" />;
  return (
    <div className="space-y-6">
      <div className={`${card} p-6`}>
        <h3 className="font-display font-semibold text-navy mb-4">Create coupon</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="text-sm text-muted-foreground">Code</label><input className={`${inputC} mt-1`} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="LAUNCH20" data-testid="coupon-code" /></div>
          <div><label className="text-sm text-muted-foreground">Percent off</label><input type="number" className={`${inputC} mt-1 w-32`} value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="20" data-testid="coupon-percent" /></div>
          <button onClick={create} className="btn-gold px-5 py-2 rounded-[3px] text-sm font-semibold flex items-center gap-1.5" data-testid="create-coupon"><Percent size={14} /> Create</button>
        </div>
      </div>
      <div className={`${card} overflow-hidden`}>
        {items.length === 0 ? <p className="p-6 text-sm text-muted-foreground">No coupons yet.</p> : items.map((c) => (
          <div key={c.code} className="flex justify-between items-center p-4 border-b border-line last:border-0">
            <div><span className="font-display font-semibold text-navy">{c.code}</span> <span className="text-sm text-muted-foreground">— {c.percent_off ? `${c.percent_off}% off` : `$${c.amount_off} off`} {!c.active && "(inactive)"}</span></div>
            {c.active && <button onClick={() => del(c.code)} className="text-red-600 text-sm font-medium">Deactivate</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
